import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck, Plus, Search, Trash2, Printer, Share2, Loader2,
  ClipboardList, X, Edit3, StickyNote, DollarSign, Wallet,
} from "lucide-react";
import { printHTML } from "../../utils/printHTML";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

/**
 * Driver-Invoices scratchpad — exclusive to the Mohammed-Hussein manager
 * (07858877756). Pure utility tool: he creates a "board" per driver,
 * jots down which customers received which invoices (amount, paid/debt
 * flag, notes), and at the end of the day clears the board or prints/
 * WhatsApp-shares it. NOTHING here touches the real orders, customer
 * balances, profits or inventory.
 */
const DriverInvoicesBoard = () => {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [newDriver, setNewDriver] = useState("");
  const [copyFromBoardId, setCopyFromBoardId] = useState(""); // optional source board
  const [adding, setAdding] = useState(false);
  // Inline form state for adding entries to the active board
  const [formCustomer, setFormCustomer] = useState("");
  const [formInvoice, setFormInvoice] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formMode, setFormMode] = useState("debt");
  const [formNotes, setFormNotes] = useState("");

  const fetchBoards = async () => {
    try {
      const { data } = await axios.get(`${API}/manager/driver-boards`);
      setBoards(data.boards || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل اللوحات");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchBoards(); }, []);

  const filteredBoards = boards.filter((b) =>
    !search.trim() ||
    (b.driver_name || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  const active = boards.find((b) => b.id === selectedId);

  const addBoard = async () => {
    const name = newDriver.trim();
    if (!name) { toast.error("اكتب اسم السائق"); return; }
    setAdding(true);
    try {
      const payload = { driver_name: name };
      if (copyFromBoardId) payload.copy_from_board_id = copyFromBoardId;
      const { data } = await axios.post(`${API}/manager/driver-boards`, payload);
      setBoards((prev) => [data.board, ...prev]);
      setSelectedId(data.board.id);
      setNewDriver("");
      setCopyFromBoardId("");
      toast.success(
        copyFromBoardId
          ? `تمت إضافة «${name}» مع نسخ ${data.board.entries.length} زبون`
          : `تمت إضافة لوحة «${name}»`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإضافة");
    } finally { setAdding(false); }
  };

  const removeBoard = async (board) => {
    if (!window.confirm(`حذف لوحة «${board.driver_name}» كاملة؟`)) return;
    try {
      await axios.delete(`${API}/manager/driver-boards/${board.id}`);
      setBoards((prev) => prev.filter((b) => b.id !== board.id));
      if (selectedId === board.id) setSelectedId(null);
      toast.success("تم الحذف");
    } catch { toast.error("فشل الحذف"); }
  };

  const clearBoard = async (board) => {
    if (!window.confirm(`تفريغ كل سطور «${board.driver_name}»؟\n(اللوحة تبقى، بس السطور تنحذف).`)) return;
    try {
      await axios.post(`${API}/manager/driver-boards/${board.id}/clear`);
      setBoards((prev) => prev.map((b) => b.id === board.id ? { ...b, entries: [] } : b));
      toast.success("تم التفريغ — جاهزة لليوم الجديد");
    } catch { toast.error("فشل التفريغ"); }
  };

  const addEntry = async () => {
    if (!active) return;
    if (!formCustomer.trim()) { toast.error("اكتب اسم الزبون"); return; }
    try {
      const { data } = await axios.post(`${API}/manager/driver-boards/${active.id}/entries`, {
        customer_name: formCustomer,
        invoice_label: formInvoice,
        amount: Number(formAmount || 0),
        mode: formMode,
        notes: formNotes,
      });
      setBoards((prev) => prev.map((b) =>
        b.id === active.id ? { ...b, entries: [...(b.entries || []), data.entry] } : b
      ));
      setFormCustomer(""); setFormInvoice(""); setFormAmount("");
      setFormNotes(""); setFormMode("debt");
      toast.success("أُضيف الزبون");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإضافة");
    }
  };

  const updateEntry = async (entry, patch) => {
    if (!active) return;
    try {
      await axios.patch(`${API}/manager/driver-boards/${active.id}/entries/${entry.id}`, patch);
      setBoards((prev) => prev.map((b) =>
        b.id !== active.id ? b : {
          ...b,
          entries: b.entries.map((e) => e.id === entry.id ? { ...e, ...patch } : e),
        }
      ));
    } catch { toast.error("فشل التحديث"); }
  };

  const removeEntry = async (entry) => {
    if (!active) return;
    if (!window.confirm(`حذف صف «${entry.customer_name}»؟`)) return;
    try {
      await axios.delete(`${API}/manager/driver-boards/${active.id}/entries/${entry.id}`);
      setBoards((prev) => prev.map((b) =>
        b.id !== active.id ? b : { ...b, entries: b.entries.filter((e) => e.id !== entry.id) }
      ));
    } catch { toast.error("فشل الحذف"); }
  };

  const totalCash = (active?.entries || []).filter((e) => e.mode === "cash").reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalDebt = (active?.entries || []).filter((e) => e.mode === "debt").reduce((s, e) => s + Number(e.amount || 0), 0);
  const total = totalCash + totalDebt;

  const buildShareText = () => {
    if (!active) return "";
    const lines = [];
    lines.push(`📋 فواتير السائق: ${active.driver_name}`);
    lines.push(`التاريخ: ${new Date().toLocaleDateString("en-GB")}`);
    lines.push("─".repeat(20));
    (active.entries || []).forEach((e, i) => {
      lines.push(
        `${i + 1}. ${e.customer_name}` +
        (e.invoice_label ? ` (${e.invoice_label})` : "") +
        `\n   ${fmt(e.amount)} د.ع — ${e.mode === "cash" ? "نقد ✓" : "دين"}` +
        (e.notes ? `\n   📝 ${e.notes}` : "")
      );
    });
    lines.push("─".repeat(20));
    lines.push(`مجموع النقد: ${fmt(totalCash)} د.ع`);
    lines.push(`مجموع الدين: ${fmt(totalDebt)} د.ع`);
    lines.push(`المجموع الكلي: ${fmt(total)} د.ع`);
    return lines.join("\n");
  };

  const shareWhatsApp = () => {
    if (!active || (active.entries || []).length === 0) {
      toast.error("لا يوجد سطور للمشاركة"); return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(buildShareText())}`, "_blank");
  };

  const printBoard = () => {
    if (!active || (active.entries || []).length === 0) {
      toast.error("لا يوجد سطور للطباعة"); return;
    }
    const rows = (active.entries || []).map((e, i) => `
      <tr>
        <td dir="ltr">${i + 1}</td>
        <td>${e.customer_name}${e.invoice_label ? ` <small>(${e.invoice_label})</small>` : ""}</td>
        <td class="num" dir="ltr">${fmt(e.amount)} د.ع</td>
        <td class="${e.mode === "cash" ? "cash" : "debt"}">${e.mode === "cash" ? "نقد ✓" : "دين"}</td>
        <td>${e.notes || "—"}</td>
      </tr>`).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>فواتير ${active.driver_name}</title>
<style>
  body { font-family: 'Tajawal', system-ui, sans-serif; padding: 20px; color: #111; direction: rtl; }
  h1 { color: #1E3A5F; margin: 0 0 4px; font-size: 22px; }
  .sub { color: #5C6E64; font-size: 12px; margin-bottom: 14px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #E4EBE6; padding: 8px; text-align: right; font-size: 13px; }
  th { background: #1E3A5F; color: white; }
  .num { font-weight: 800; color: #1E3A5F; }
  .cash { color: #15803D; font-weight: 800; }
  .debt { color: #B91C1C; font-weight: 800; }
  .totals { margin-top: 18px; padding: 12px; background: #F0F9FF; border: 2px solid #BAE6FD; border-radius: 12px; }
  .totals b { color: #1E3A5F; }
</style></head><body>
  <h1>📋 فواتير السائق: ${active.driver_name}</h1>
  <div class="sub">التاريخ: <span dir="ltr">${new Date().toLocaleDateString("en-GB")}</span> · عدد السطور: <span dir="ltr">${(active.entries || []).length}</span></div>
  <table>
    <thead><tr><th>#</th><th>اسم الزبون</th><th>المبلغ</th><th>الحالة</th><th>ملاحظة</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="totals">
    <p><b>مجموع النقد:</b> <span dir="ltr">${fmt(totalCash)} د.ع</span></p>
    <p><b>مجموع الدين:</b> <span dir="ltr">${fmt(totalDebt)} د.ع</span></p>
    <p><b>المجموع الكلي:</b> <span dir="ltr">${fmt(total)} د.ع</span></p>
  </div>
</body></html>`;
    printHTML(html, { title: `فواتير ${active.driver_name}` });
  };

  if (loading) return (
    <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>
  );

  return (
    <div className="space-y-3" dir="rtl" data-testid="driver-invoices-board">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <Truck className="w-5 h-5" />
          <h3 className="font-bold text-lg">فواتير السواق</h3>
        </div>
        <p className="text-xs opacity-90">
          سجّل مسوّدة لكل سائق — أسماء الزبائن والمبالغ. مستقلّة تماماً عن الحسابات والأرصدة الرئيسية.
        </p>
      </div>

      {/* Add + search */}
      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="flex gap-2">
            <input
              value={newDriver}
              onChange={(e) => setNewDriver(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addBoard()}
              placeholder="أضف سائق (اكتب الاسم)"
              data-testid="add-driver-input"
              className="flex-1 px-3 py-2 rounded-xl border-2 border-[#3B82F6]"
            />
            <button
              onClick={addBoard}
              disabled={adding}
              data-testid="add-driver-btn"
              className="bg-[#15803D] hover:bg-[#126b32] disabled:opacity-60 text-white font-bold px-4 py-2 rounded-full flex items-center gap-1"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              إضافة
            </button>
          </div>
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="بحث عن سائق…"
              data-testid="search-driver-input"
              className="w-full pr-9 pl-3 py-2 rounded-xl border-2 border-[#E4EBE6]"
            />
          </div>
        </div>
        {/* Optional: copy entries from another driver. Lets the manager
            re-use the same customer list when a new driver takes over. */}
        {boards.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-[#5C6E64] shrink-0">نسخ من سائق سابق:</label>
            <select
              value={copyFromBoardId}
              onChange={(e) => setCopyFromBoardId(e.target.value)}
              data-testid="copy-from-select"
              className="flex-1 px-3 py-1.5 rounded-xl border-2 border-[#E4EBE6] text-sm"
            >
              <option value="">— بدون نسخ (لوحة فارغة) —</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.driver_name} ({(b.entries || []).length} زبون)
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Boards grid (when no active) OR active board */}
      {!active ? (
        filteredBoards.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed border-[#E4EBE6]">
            <Truck className="w-12 h-12 mx-auto text-[#BAE6FD] mb-2" />
            <p className="font-bold text-[#111814]">لا يوجد سواق بعد</p>
            <p className="text-xs text-[#5B6B62]">أضف سائق من الأعلى لتبدأ.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredBoards.map((b) => {
              const count = (b.entries || []).length;
              const sum = (b.entries || []).reduce((s, e) => s + Number(e.amount || 0), 0);
              return (
                <div
                  key={b.id}
                  data-testid={`board-card-${b.id}`}
                  className="bg-white rounded-2xl p-4 border-2 border-[#E4EBE6] hover:border-[#3B82F6] cursor-pointer transition-colors group"
                  onClick={() => setSelectedId(b.id)}
                >
                  <div className="flex items-start gap-2">
                    <div className="bg-[#1E3A5F] text-white rounded-xl p-2.5 shrink-0">
                      <Truck className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#111814] truncate">{b.driver_name}</p>
                      <p className="text-xs text-[#5B6B62]">{count} زبون</p>
                      <p className="text-sm font-extrabold text-[#15803D]" dir="ltr">{fmt(sum)} د.ع</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeBoard(b); }}
                      data-testid={`del-board-${b.id}`}
                      className="opacity-0 group-hover:opacity-100 text-[#E85D45] hover:bg-[#FEE2E2] rounded-full p-1.5 transition-all"
                      title="حذف اللوحة"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div className="bg-white rounded-2xl border-2 border-[#3B82F6] overflow-hidden">
          {/* Active-board header */}
          <div className="bg-[#1E3A5F] text-white p-3 flex items-center gap-2">
            <Truck className="w-5 h-5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{active.driver_name}</p>
              <p className="text-[10px] opacity-90">
                {(active.entries || []).length} زبون · <span dir="ltr">{fmt(total)} د.ع</span>
              </p>
            </div>
            <button
              onClick={() => setSelectedId(null)}
              data-testid="board-back-btn"
              className="bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              رجوع
            </button>
          </div>

          {/* Quick-add form */}
          <div className="p-3 bg-[#F0F9FF] border-b-2 border-[#BAE6FD]">
            <p className="text-xs font-bold text-[#1E3A5F] mb-2">إضافة زبون جديد:</p>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
              <input
                value={formCustomer}
                onChange={(e) => setFormCustomer(e.target.value)}
                placeholder="اسم الزبون *"
                data-testid="entry-customer-input"
                className="sm:col-span-3 px-3 py-2 rounded-xl border-2 border-[#3B82F6] text-sm"
              />
              <input
                value={formInvoice}
                onChange={(e) => setFormInvoice(e.target.value)}
                placeholder="رقم/اسم الفاتورة"
                data-testid="entry-invoice-input"
                className="sm:col-span-2 px-3 py-2 rounded-xl border-2 border-[#E4EBE6] text-sm"
              />
              <input
                type="number"
                value={formAmount}
                onChange={(e) => setFormAmount(e.target.value)}
                placeholder="المبلغ"
                data-testid="entry-amount-input"
                className="sm:col-span-2 px-3 py-2 rounded-xl border-2 border-[#E4EBE6] text-sm"
              />
              <select
                value={formMode}
                onChange={(e) => setFormMode(e.target.value)}
                data-testid="entry-mode-select"
                className="sm:col-span-2 px-3 py-2 rounded-xl border-2 border-[#E4EBE6] text-sm"
              >
                <option value="debt">دين</option>
                <option value="cash">نقد</option>
              </select>
              <input
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="ملاحظة (اختياري)"
                data-testid="entry-notes-input"
                className="sm:col-span-2 px-3 py-2 rounded-xl border-2 border-[#E4EBE6] text-sm"
              />
              <button
                onClick={addEntry}
                data-testid="add-entry-btn"
                className="sm:col-span-1 bg-[#15803D] hover:bg-[#126b32] text-white font-bold rounded-xl flex items-center justify-center gap-1 py-2"
              >
                <Plus className="w-4 h-4" /> حفظ
              </button>
            </div>
          </div>

          {/* Entries table */}
          <div className="overflow-x-auto">
            {(active.entries || []).length === 0 ? (
              <div className="text-center py-10 text-[#5B6B62]">
                <ClipboardList className="w-10 h-10 mx-auto mb-2 text-[#BAE6FD]" />
                <p className="text-sm">ما عدنا أي زبون لهالسائق — أضف من فوق.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[#1E3A5F]">
                  <tr>
                    <th className="px-2 py-2 text-right">#</th>
                    <th className="px-2 py-2 text-right">اسم الزبون</th>
                    <th className="px-2 py-2 text-right">الفاتورة</th>
                    <th className="px-2 py-2 text-right">المبلغ</th>
                    <th className="px-2 py-2 text-right">الحالة</th>
                    <th className="px-2 py-2 text-right">ملاحظة</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(active.entries || []).map((entry, idx) => (
                    <tr key={entry.id} className="border-t border-[#E4EBE6] hover:bg-[#F8FAFC]" data-testid={`entry-row-${entry.id}`}>
                      <td dir="ltr" className="px-2 py-2 text-center text-xs text-[#5B6B62]">{idx + 1}</td>
                      <td className="px-2 py-2">
                        <input
                          defaultValue={entry.customer_name}
                          onBlur={(e) => e.target.value !== entry.customer_name && updateEntry(entry, { customer_name: e.target.value })}
                          className="bg-transparent w-full focus:bg-white focus:border-[#3B82F6] border border-transparent rounded px-1 py-0.5 text-sm font-bold"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          defaultValue={entry.invoice_label || ""}
                          onBlur={(e) => e.target.value !== entry.invoice_label && updateEntry(entry, { invoice_label: e.target.value })}
                          className="bg-transparent w-full focus:bg-white focus:border-[#3B82F6] border border-transparent rounded px-1 py-0.5 text-xs"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          defaultValue={entry.amount}
                          onBlur={(e) => Number(e.target.value) !== Number(entry.amount) && updateEntry(entry, { amount: Number(e.target.value) })}
                          className="bg-transparent w-24 focus:bg-white focus:border-[#3B82F6] border border-transparent rounded px-1 py-0.5 text-sm font-bold text-[#1E3A5F]"
                          dir="ltr"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <select
                          value={entry.mode}
                          onChange={(e) => updateEntry(entry, { mode: e.target.value })}
                          className={`text-xs font-bold rounded px-2 py-1 border border-transparent focus:border-[#3B82F6] ${entry.mode === "cash" ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEE2E2] text-[#B91C1C]"}`}
                        >
                          <option value="debt">دين</option>
                          <option value="cash">نقد ✓</option>
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <input
                          defaultValue={entry.notes || ""}
                          onBlur={(e) => e.target.value !== entry.notes && updateEntry(entry, { notes: e.target.value })}
                          placeholder="—"
                          className="bg-transparent w-full focus:bg-white focus:border-[#3B82F6] border border-transparent rounded px-1 py-0.5 text-xs"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => removeEntry(entry)}
                          data-testid={`del-entry-${entry.id}`}
                          className="text-[#E85D45] hover:bg-[#FEE2E2] rounded-full p-1"
                          title="حذف"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Totals + actions */}
          <div className="p-3 border-t-2 border-[#E4EBE6] bg-[#F8FAFC]">
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-[#DCFCE7] border border-[#86EFAC] rounded-xl p-2 text-center">
                <p className="text-[10px] text-[#15803D] font-bold">نقد</p>
                <p className="text-sm font-extrabold text-[#15803D]" dir="ltr">{fmt(totalCash)}</p>
              </div>
              <div className="bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl p-2 text-center">
                <p className="text-[10px] text-[#B91C1C] font-bold">دين</p>
                <p className="text-sm font-extrabold text-[#B91C1C]" dir="ltr">{fmt(totalDebt)}</p>
              </div>
              <div className="bg-[#DBEAFE] border border-[#93C5FD] rounded-xl p-2 text-center">
                <p className="text-[10px] text-[#1E3A5F] font-bold">المجموع</p>
                <p className="text-sm font-extrabold text-[#1E3A5F]" dir="ltr">{fmt(total)}</p>
              </div>
            </div>

            {/* End-of-day SUMMARY notes — quick at-a-glance list of every
                customer name + their amount, plus the grand total. The
                manager uses this card to mentally tally what's still owed
                before clearing the board. */}
            {(active.entries || []).length > 0 && (
              <div className="bg-white border-2 border-[#1E3A5F] rounded-2xl p-3 mb-3">
                <div className="flex items-center gap-2 mb-2 border-b border-[#E4EBE6] pb-2">
                  <StickyNote className="w-4 h-4 text-[#1E3A5F]" />
                  <p className="font-bold text-sm text-[#1E3A5F]">
                    ملخص اليوم — {active.driver_name}
                  </p>
                </div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {(active.entries || []).map((e, i) => (
                    <div
                      key={`sum-${e.id}`}
                      className="flex items-center justify-between text-xs gap-2 py-1 px-2 rounded hover:bg-[#F0F9FF]"
                    >
                      <span className="text-[#5B6B62] shrink-0" dir="ltr">{i + 1}.</span>
                      <span className="flex-1 font-bold text-[#111814] truncate" title={e.customer_name}>
                        {e.customer_name}
                        {e.invoice_label && <span className="text-[#5B6B62] font-normal"> · {e.invoice_label}</span>}
                      </span>
                      <span
                        className={`font-extrabold shrink-0 ${e.mode === "cash" ? "text-[#15803D]" : "text-[#B91C1C]"}`}
                        dir="ltr"
                      >
                        {fmt(e.amount)}
                        <span className="text-[9px] opacity-70 mr-1">{e.mode === "cash" ? "نقد" : "دين"}</span>
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t-2 border-[#1E3A5F] mt-2 pt-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-[#1E3A5F]">
                    الإجمالي ({(active.entries || []).length} زبون):
                  </span>
                  <span className="text-base font-extrabold text-[#1E3A5F]" dir="ltr">
                    {fmt(total)} د.ع
                  </span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={printBoard}
                data-testid="print-board-btn"
                className="bg-[#1E3A5F] hover:bg-[#152a45] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1"
              >
                <Printer className="w-3.5 h-3.5" /> طباعة
              </button>
              <button
                onClick={shareWhatsApp}
                data-testid="share-board-btn"
                className="bg-[#25D366] hover:bg-[#1ebe57] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1"
              >
                <Share2 className="w-3.5 h-3.5" /> واتساب
              </button>
              <button
                onClick={() => clearBoard(active)}
                data-testid="clear-board-btn"
                className="bg-[#F59E0B] hover:bg-[#d97706] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1 ms-auto"
                title="حذف جميع السطور (اللوحة تبقى)"
              >
                <Trash2 className="w-3.5 h-3.5" /> تفريغ اليوم
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverInvoicesBoard;
